import { Component, ApplicationRef, forwardRef, input, output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgxFileDropEntry, FileSystemFileEntry, NgxFileDropModule } from 'ngx-file-drop';

@Component({
    selector: 'ws-file-picker',
    templateUrl: './filePicker.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FilePickerComponent),
            multi: true,
        },
    ],
    imports: [NgxFileDropModule],
})
export class FilePickerComponent implements ControlValueAccessor {
    constructor(private _applicationRef: ApplicationRef) {}

    /**
     * Allow selecting multiple files.
     */
    readonly multiple = input<boolean>(false);

    /**
     * Limit the types of files that can be picked
     * ex: ".xls,.xlsx"
     */
    readonly acceptTypes = input<string>(null);

    /**
     * Hides name of files loaded
     */
    readonly hideFileNames = input<boolean>(false);

    /**
     * Event emitter for when the file selection changes.
     */
    readonly picked = output<File[]>();

    /**
     * The name of the selected file or a display message for how many files are selected (if multiple allowed).
     */
    filename: string;

    /**
     * Called by Angular forms when the value is set programmatically.
     */
    writeValue(files: File[]): void {
        this._updateFilename(files ?? []);
    }

    registerOnChange(fn: (value: File[]) => void): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this._onTouched = fn;
    }

    /**
     * Gets called when the user changes the selected file (via drag-and-drop or browse).
     * @param uploadFiles The event from ngx-file-drop
     */
    fileDropped(uploadFiles: NgxFileDropEntry[]): void {
        const limit = this.multiple() ? uploadFiles.length : 1;
        const files: File[] = [];

        for (let i = 0; i < limit; i++) {
            const file = uploadFiles[i];
            if (file && file.fileEntry.isFile) {
                const fileEntry = file.fileEntry as FileSystemFileEntry;
                fileEntry.file((f: File) => {
                    files.push(f);

                    // This process is async so we need to check in this callback to determine when we are done
                    if (files.length === limit) {
                        this._updateFilename(files);
                        this._onChange(files);
                        this._onTouched();
                        this.picked.emit(files);
                        // let angular know that the file has changed
                        this._applicationRef.tick();
                    }
                });
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private _onChange: (value: File[]) => void = () => {};
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private _onTouched: () => void = () => {};

    private _updateFilename(files: File[]): void {
        if (files.length === 0) {
            this.filename = null;
        } else if (files.length === 1) {
            this.filename = files[0].name;
        } else {
            this.filename = `${files.length} files selected`;
        }
    }
}
